import { AgentDesktopRouter } from '@agent/agent-interface/agent-desktop-router';
import { ChatSocketService } from '@agent/agent-interface/contact-view/chat/services/chat-socket.service';
import { TransferDeprecatedService } from '@agent/core/contact/transfer/transfer-deprecated.service';
import { ConferenceDeprecatedService } from '@agent/core/phone/conference/conference-deprecated.service';
import { GuestState, GuestType } from '@agent/core/phone/conference/models/guest';
import { Component, Inject, Input, OnInit } from '@angular/core';
import { NG1_CONFERENCE, NG1_TRANSFER } from '@commons/core/core.provider.ajs';
import { filter, first } from 'rxjs';

import {
  BookingFacade,
  ChannelType,
  DisplayDeprecatedComponent,
  Logger,
  ToastClass,
  ToasterFacade,
  TranslateService,
  UntilDestroy,
} from '@contact/upgrade';

@UntilDestroy()
@Component({
  selector: 'chat-transfer-actions',
  templateUrl: 'chat-transfer-actions.component.html',
  styleUrls: ['./chat-transfer-actions.component.scss'],
})
export class ChatTransferActionsComponent extends DisplayDeprecatedComponent implements OnInit {
  @Input()
  responseId: string;

  @Input()
  private isConference: boolean;

  public toggleMoreMenu: boolean = false;

  private isTransferDone: boolean = false;

  constructor(
    private readonly logger: Logger,
    private readonly translateService: TranslateService,
    @Inject(NG1_TRANSFER) protected readonly transferDeprecatedService: TransferDeprecatedService,
    private readonly toasterFacade: ToasterFacade,
    @Inject(NG1_CONFERENCE) protected readonly conferenceDeprecatedService: ConferenceDeprecatedService,
    private readonly chatSocketService: ChatSocketService,
    private readonly contactFacade: BookingFacade,
    private readonly router: AgentDesktopRouter
  ) {
    super();
  }

  get canValidate(): boolean {
    return this.transferDeprecatedService.canValidateTransfer(this.responseId, ChannelType.CHAT);
  }

  ngOnInit(): void {
    this.conferenceDeprecatedService
      .guestState$()
      .pipe(
        filter(guest => guest.responseId === this.responseId),
        filter(guest => guest.type === GuestType.TARGET),
        filter(guest => guest.state === GuestState.CONNECTED),
        filter(guest => !guest.removed),
        first()
      )
      .subscribe(guest => {
        this.isTransferDone = true;
        if (this.isConference) {
          let transfer = this.transferDeprecatedService.getTransfer(this.responseId).get();
          this.toasterFacade.notification({
            message: this.translateService.instant('agent:toast.chat_conference_transferred', { name: transfer.target.name }),
            cssClass: ToastClass.CHAT,
            duration: 5000,
          });
          const chat = this.chatSocketService.getChatByResponseId(this.responseId);
          if (chat) {
            chat.conferenceCsrName = transfer.target.name;
          }
          this.router.navigateFromLastChild(['../..']);
        } else {
          this.validateTransfer(guest.id);
        }
      });

    this.conferenceDeprecatedService
      .getTargetGuests(this.responseId)
      .filter(guest => guest.type === GuestType.TARGET)
      .filter(guest => guest.state === GuestState.CONNECTED)
      .forEach(guest => {
        this.isTransferDone = true;
        if (this.isConference) {
          let transfer = this.transferDeprecatedService.getTransfer(this.responseId).get();
          this.toasterFacade.notification({
            message: this.translateService.instant('agent:toast.chat_conference_transferred', { name: transfer.target.name }),
            cssClass: ToastClass.CHAT,
            duration: 5000,
          });
          const chat = this.chatSocketService.getChatByResponseId(this.responseId);
          if (chat) {
            chat.conferenceCsrName = transfer.target.name;
          }
          this.router.navigateFromLastChild(['../..']);
        } else {
          this.validateTransfer(guest.id);
        }
      });
  }

  ngOnDestroy(): void {
    if (!this.isTransferDone) {
      const chat = this.chatSocketService.getChatByResponseId(this.responseId);
      if (chat) {
        chat.inConferenceMode = false;
        chat.inTransferMode = false;
        chat.conference = false;
      }

      this.transferDeprecatedService.cancelTransfer(this.responseId).then(() => {
        this.contactFacade.update(this.responseId, {
          conference: false,
        });
      });
    }
  }

  validate(): void {
    if (this.canValidate) {
      let transfer = this.transferDeprecatedService.getTransfer(this.responseId).get();
      transfer.ignoreConference = !this.isConference;
      this.transferDeprecatedService.updateTransfer(transfer);

      const chat = this.chatSocketService.getChatByResponseId(this.responseId);
      if (chat) {
        chat.conferenceCsrName = transfer.target.name;
      }
      this.transferDeprecatedService.validateTransfer(this.responseId).then(
        response => {
          if (!response || response.status !== 200) {
            // if error in http call
            this.toasterFacade.alert({ message: this.translateService.instant('agent:contact.workspace.transfer.toast.error') });
          } else {
            this.logger.debug('[contactChatTransferActions]Transfer request sent');
            const transferMessage = transfer.comment;
            if (transferMessage) {
              this.chatSocketService.sendTransferCommentMessage(transfer.comment);
            }
            if (transfer.targetType === 'SKILL') {
              this.toasterFacade.notification({
                message: 'agent:contact.workspace.transfer.toast.complete',
                cssClass: ToastClass.CHAT,
                duration: 5000,
              });
            }
          }
        },
        () => {
          // if error in http call
          this.toasterFacade.alert({ message: this.translateService.instant('agent:contact.workspace.transfer.toast.error') });
        }
      );
    } else {
      this.logger.info('[contactChatTransferActions]transfer action already in progress');
    }
  }

  cancel(): void {
    const chat = this.chatSocketService.getChatByResponseId(this.responseId);
    if (chat) {
      chat.inConferenceMode = false;
      chat.inTransferMode = false;
      chat.conference = false;
    }

    this.transferDeprecatedService.cancelTransfer(this.responseId).then(() => {
      this.contactFacade.update(this.responseId, {
        conference: false,
      });
      this.router.navigateFromLastChild(['../..']);
    });
  }

  validateTransfer(guestId: string): void {
    this.conferenceDeprecatedService.validateTransfer(this.responseId, guestId).then(() => {
      this.toasterFacade.notification({
        message: 'agent:contact.workspace.transfer.toast.complete',
        cssClass: ToastClass.CHAT,
        duration: 5000,
      });
    });
  }

  get isDialingTarget(): boolean {
    return this.transferDeprecatedService.isDialingTarget(this.responseId);
  }
}
